import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PaymentMethod, PaymentMethodService, PaymentMethodType } from '@galaxy/billing';
import { firstValueFrom } from 'rxjs';
import { SelectPaymentMethodDialogData, SelectedPaymentMethod } from '../payment-method';
import { SelectPaymentMethodDialogComponent } from '../select-payment-method-dialog/select-payment-method-dialog.component';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { PaymentMethodDisplayComponent } from '../payment-method-display/payment-method-display.component';
import { PaymentTypeToTitlePipe } from '../select-payment-method-dialog/payment-type-to-title.pipe';
import { KeysPipe } from '../select-payment-method-dialog/keys.pipe';

@Component({
  imports: [
    NgIf,
    NgForOf,
    NgClass,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatFormFieldModule,
    GalaxyBadgeModule,
    PaymentMethodDisplayComponent,
    TranslateModule,
    PaymentTypeToTitlePipe,
    KeysPipe,
  ],
  providers: [TranslateService],
  selector: 'smb-invoicing-payment-method-selector',
  templateUrl: './payment-method-selector.component.html',
  styleUrls: ['./payment-method-selector.component.scss'],
})
export class PaymentMethodSelectorComponent implements OnChanges, OnInit {
  @Input() stripeConnectId: string;
  @Input() merchantId: string;
  @Input() customerId: string;
  @Input() userId: string;
  @Input() stripeKey: string;
  @Input() editButton: string;
  @Input() makeDefaultButton?: string;
  @Input() addCardButton: string;
  @Input() isEditable: boolean;
  @Input() hintText: string;
  @Input() emptyCardHintText: string;
  @Input() submitDialogText: string;
  @Input() paymentMethods: PaymentMethod[];
  @Input() defaultPaymentMethod: PaymentMethod;
  @Input() canRemoveSelectedMethod: boolean;
  @Input() showAllMethods: boolean;

  @Output() selectedPaymentMethod: EventEmitter<SelectedPaymentMethod> = new EventEmitter<SelectedPaymentMethod>();

  sortedMethods: Map<PaymentMethodType, PaymentMethod[]>;

  constructor(
    public dialog: MatDialog,
    private paymentMethodService: PaymentMethodService,
    private cd: ChangeDetectorRef,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    if (!this.makeDefaultButton) {
      this.makeDefaultButton = this.translateService.instant('PAYMENT_METHOD_SELECTOR.USE_AS_DEFAULT');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.paymentMethods?.previousValue !== changes.paymentMethods?.currentValue) {
      this.sortedMethods = this._groupAndSortPaymentMethods(this.paymentMethods);
    }
  }

  public openPaymentMethodDialog(): void {
    this.dialog
      .open(SelectPaymentMethodDialogComponent, {
        width: '450px',
        data: {
          accountId: this.stripeConnectId,
          merchantId: this.merchantId,
          customerId: this.customerId,
          userId: this.userId,
          stripeKey: this.stripeKey,
          paymentMethods: this.sortedMethods,
          submitDialogText: this.submitDialogText,
          canRemoveSelectedMethod: this.canRemoveSelectedMethod,
          defaultPaymentMethod: this.defaultPaymentMethod?.details?.id,
        } as SelectPaymentMethodDialogData,
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((m) => {
        this.selectedPaymentMethod.emit(m);
      });
  }

  public addPaymentMethodDialog(): void {
    this.dialog
      .open(SelectPaymentMethodDialogComponent, {
        width: '450px',
        data: {
          accountId: this.stripeConnectId,
          merchantId: this.merchantId,
          customerId: this.customerId,
          userId: this.userId,
          stripeKey: this.stripeKey,
          submitDialogText: this.submitDialogText,
          canRemoveSelectedMethod: false,
          hideExistingMethods: true,
        } as SelectPaymentMethodDialogData,
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((m) => {
        this.selectedPaymentMethod.emit(m);
      });
  }

  public async makePaymentMethodDefault(paymentMethod: PaymentMethod): Promise<void> {
    this.selectedPaymentMethod.emit({ paymentMethodId: paymentMethod.details?.id, newMethod: false });
  }

  public async deletePaymentMethod(paymentMethod: PaymentMethod): Promise<void> {
    try {
      await firstValueFrom(this.paymentMethodService.deletePaymentMethod(this.merchantId, paymentMethod.details?.id));
    } catch {
      this.snackbarService.openErrorSnack('PAYMENT_METHOD_SELECTOR.ERROR_REMOVING_PAYMENT_METHOD');
      return;
    }

    const type = paymentMethod.type;
    const methods = this.sortedMethods.get(type) || [];

    if (!methods) {
      return;
    }

    const index = methods.findIndex((m) => m.details?.id === paymentMethod.details?.id);
    if (index > -1) {
      methods.splice(index, 1);
      methods.length === 0 ? this.sortedMethods.delete(type) : this.sortedMethods.set(type, methods);
    }

    this.sortedMethods = new Map(this.sortedMethods);
    this.cd.detectChanges();
    return;
  }

  private _typeSortPriority(type: PaymentMethodType): number {
    switch (type) {
      case PaymentMethodType.CARD:
        return 1;
      case PaymentMethodType.ACH_DEBIT:
        return 2;
      case PaymentMethodType.ACSS_DEBIT:
        return 3;
      default:
        return 4;
    }
  }

  private _groupAndSortPaymentMethods(methods: PaymentMethod[]): Map<PaymentMethodType, PaymentMethod[]> {
    const grouped = new Map<PaymentMethodType, PaymentMethod[]>();

    methods.sort((a, b) => {
      const typePriorityA = this._typeSortPriority(a.type);
      const typePriorityB = this._typeSortPriority(b.type);
      if (typePriorityA !== typePriorityB) {
        return typePriorityA - typePriorityB;
      }
      const isADefault = a.details?.id === this.defaultPaymentMethod?.details?.id ? 1 : 0;
      const isBDefault = b.details?.id === this.defaultPaymentMethod?.details?.id ? 1 : 0;
      return isBDefault - isADefault;
    });

    methods.forEach((method) => {
      const group = grouped.get(method.type) || [];
      group.push(method);
      grouped.set(method.type, group);
    });

    return grouped;
  }
}
